import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Star, EyeOff, Eye, Loader2, Shield } from "lucide-react";
import { toast } from "sonner";

const FILTERS = [
    { key: "all", label: "Alle" },
    { key: "visible", label: "Sichtbar" },
    { key: "hidden", label: "Ausgeblendet" },
];

function Stars({ value }) {
    return (
        <div className="inline-flex items-center gap-0.5" data-testid="admin-review-stars">
            {[1, 2, 3, 4, 5].map((n) => (
                <Star
                    key={n}
                    className={`w-3.5 h-3.5 ${n <= value ? "fill-[#FFCC00] text-[#FFCC00]" : "text-gray-300"}`}
                />
            ))}
        </div>
    );
}

export default function AdminReviewsPage() {
    const { user, loading: authLoading } = useAuth();
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("all");
    const [flaggedOnly, setFlaggedOnly] = useState(false);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ hidden: filter });
            if (flaggedOnly) params.set("flagged_only", "true");
            const { data } = await api.get(`/admin/reviews?${params.toString()}`);
            setItems(data.items || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Fehler beim Laden");
        } finally {
            setLoading(false);
        }
    }, [filter, flaggedOnly]);

    useEffect(() => {
        if (user?.role === "admin") load();
    }, [user, load]);

    const toggle = async (review) => {
        setBusyId(review.id);
        try {
            const action = review.hidden ? "unhide" : "hide";
            await api.post(`/admin/reviews/${review.id}/${action}`);
            toast.success(review.hidden ? "Bewertung wieder eingeblendet" : "Bewertung ausgeblendet");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen");
        } finally {
            setBusyId(null);
        }
    };

    if (authLoading) return <div className="p-8 text-sm text-gray-500">Lädt…</div>;
    if (!user || user.role !== "admin") {
        return (
            <div className="max-w-3xl mx-auto px-4 py-12 text-center" data-testid="admin-reviews-forbidden">
                <Shield className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <h1 className="font-display text-2xl font-bold">Zugriff verweigert</h1>
                <p className="text-sm text-gray-600 mt-2">Nur Admins können diese Seite öffnen.</p>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-8 md:py-12" data-testid="admin-reviews-page">
            <div className="mb-6">
                <div className="text-xs font-bold uppercase tracking-wider text-[#FFCC00] mb-1">Admin</div>
                <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight">Bewertungs-Moderation</h1>
                <p className="text-gray-600 mt-2 text-sm">
                    Ausgeblendete Bewertungen erscheinen nicht mehr öffentlich und fließen nicht in den Durchschnitt ein.
                    Inhalte bleiben für Streitschlichtung gespeichert.
                </p>
            </div>

            <div className="flex gap-2 mb-6 flex-wrap" data-testid="admin-reviews-filters">
                {FILTERS.map((f) => (
                    <button
                        key={f.key}
                        onClick={() => setFilter(f.key)}
                        className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-sm transition-colors ${
                            filter === f.key
                                ? "bg-[#111827] text-white"
                                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                        data-testid={`admin-reviews-filter-${f.key}`}
                    >
                        {f.label}
                    </button>
                ))}
                <button
                    onClick={() => setFlaggedOnly((v) => !v)}
                    className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-sm transition-colors ml-auto ${
                        flaggedOnly ? "bg-red-700 text-white" : "bg-red-50 text-red-800 border border-red-300 hover:bg-red-100"
                    }`}
                    data-testid="admin-reviews-filter-flagged"
                    title="Nur KI-flaggierte Bewertungen (suspicious_score ≥ 60)"
                >
                    ⚠ KI-Flag {flaggedOnly ? "✓" : ""}
                </button>
            </div>

            {loading ? (
                <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> Lädt…</div>
            ) : items.length === 0 ? (
                <div className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-sm p-6 text-center" data-testid="admin-reviews-empty">
                    Keine Bewertungen in dieser Ansicht.
                </div>
            ) : (
                <div className="space-y-3" data-testid="admin-reviews-list">
                    {items.map((r) => (
                        <div
                            key={r.id}
                            className={`bg-white border rounded-sm p-4 ${r.hidden ? "border-amber-300 bg-amber-50/40" : "border-gray-200"}`}
                            data-testid={`admin-review-row-${r.id}`}
                        >
                            <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap mb-1">
                                        <Stars value={r.rating} />
                                        <span className="text-sm font-bold text-[#111827]">{r.title || "(ohne Titel)"}</span>
                                        {r.hidden && (
                                            <Badge className="bg-amber-200 text-amber-900 hover:bg-amber-200 uppercase tracking-wider text-[9px] font-bold rounded-sm">
                                                Ausgeblendet
                                            </Badge>
                                        )}
                                        {r.ai_flagged && (
                                            <Badge
                                                className="bg-red-100 text-red-800 hover:bg-red-100 uppercase tracking-wider text-[9px] font-bold rounded-sm border border-red-300"
                                                data-testid={`admin-review-flag-${r.id}`}
                                                title={(r.ai_moderation?.reasons || []).join(" · ") || "KI-Verdacht"}
                                            >
                                                ⚠ KI-Flag · {r.ai_moderation?.suspicious_score || 0}
                                            </Badge>
                                        )}
                                        {r.ai_moderation?.sentiment && (
                                            <Badge
                                                className={`text-[9px] font-bold uppercase tracking-wider rounded-sm border hover:bg-opacity-100 ${
                                                    r.ai_moderation.sentiment === "positive" ? "bg-emerald-50 text-emerald-800 border-emerald-200" :
                                                    r.ai_moderation.sentiment === "negative" ? "bg-red-50 text-red-700 border-red-200" :
                                                    "bg-gray-50 text-gray-700 border-gray-200"
                                                }`}
                                                data-testid={`admin-review-sentiment-${r.id}`}
                                            >
                                                {r.ai_moderation.sentiment}
                                            </Badge>
                                        )}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                        Händler: <Link to={`/dealer/${r.dealer_id}`} className="font-semibold text-gray-800 hover:underline">{r.dealer_name || r.dealer_id}</Link>
                                        {" · "}von <strong className="text-gray-700">{r.reviewer_name}</strong>
                                        {" · "}<span className="font-mono-num">{new Date(r.created_at).toLocaleDateString("de-DE")}</span>
                                    </div>
                                </div>
                                <Button
                                    onClick={() => toggle(r)}
                                    disabled={busyId === r.id}
                                    variant="outline"
                                    className="rounded-sm font-bold uppercase text-xs tracking-wider"
                                    data-testid={`admin-review-toggle-${r.id}`}
                                >
                                    {busyId === r.id ? (
                                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    ) : r.hidden ? (
                                        <><Eye className="w-3.5 h-3.5 mr-1" /> Einblenden</>
                                    ) : (
                                        <><EyeOff className="w-3.5 h-3.5 mr-1" /> Ausblenden</>
                                    )}
                                </Button>
                            </div>
                            {r.comment && (
                                <p className="text-sm text-gray-700 whitespace-pre-wrap mt-2 border-t border-gray-100 pt-2">{r.comment}</p>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
